import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { createSupportAiAndBaseSubscription } from "./schema/billing/create-support-ai-and-base-subscription";
import { createSupportAiSubscription } from "./schema/billing/create-support-ai-subscription";
import { createSupportBaseSubscription } from "./schema/billing/create-support-base-subscription";
import { createSupportPhoneSubscription } from "./schema/billing/create-support-phone-subscription";
import { getPreferredPaymentProvider } from "./schema/billing/get-preferred-payment-provider";
import { checkMetaDeletionStatus } from "./schema/check-meta-deletion-status";
import { configureMerchantEmail } from "./schema/email/configure-merchant-email";
import { deleteSenderEmail } from "./schema/email/delete-sender-email";
import { getMerchantDnsValues } from "./schema/email/get-merchant-dns-values";
import { setDefaultEmail } from "./schema/email/set-default-email";
import { updateSenderEmail } from "./schema/email/update-sender-email";
import { verifyMerchantEmail } from "./schema/email/verify-merchant-email";
import { generateCopilotResponse } from "./schema/generate-copilot-response";
import { getBaseSupportPredictedPriceInfo } from "./schema/get-base-support-predicted-price-info";
import { getShopperAiDashboardMetrics } from "./schema/get-concierge-dashboard-metrics";
import { getConversationInsightsAnalytics } from "./schema/get-conversation-insights-analytics";
import { getCustomerSuggestions } from "./schema/get-customer-suggestions";
import { getLlmThread } from "./schema/get-llm-thread";
import { getShopperAiSettingsOptimization } from "./schema/get-shopper-ai-settings-optimization";
import { getRechargeIntegration } from "./schema/integrations/get-recharge-integration";
import { upsertRechargeIntegration } from "./schema/integrations/upsert-recharge-integration";
import { lookupProductsByIds } from "./schema/lookup-products-by-ids";
import { markConversationsSpam } from "./schema/mark-conversations-spam";
import { regenerateConversationInsights } from "./schema/regenerate-conversation-insights";
import { cancelSubscription } from "./schema/subscriptions/cancel-subscription";
import { editSubscription } from "./schema/subscriptions/edit-subscription";
import { editSubscriptionAddress } from "./schema/subscriptions/edit-subscription-address";
import { getCustomerSubscriptions } from "./schema/subscriptions/get-customer-subscriptions";
import { getCustomerSubscriptionTotalSpent } from "./schema/subscriptions/get-customer-total-spent";
import { skipSubscription } from "./schema/subscriptions/skip-subscription";
import { supportAnalytics } from "./schema/support-analytics";
import { updateGmailIntegrationName } from "./schema/update-gmail-integration-name";
import { upsertShopperAiSettingsOptimization } from "./schema/upsert-shopper-ai-settings-optimization";

const billingEndpoints = {
  createSupportAiSubscription,
  createSupportBaseSubscription,
  createSupportPhoneSubscription,
  createSupportAiAndBaseSubscription,
  getPreferredPaymentProvider,
};

const emailEndpoints = {
  configureMerchantEmail,
  verifyMerchantEmail,
  getMerchantDnsValues,
  updateSenderEmail,
  deleteSenderEmail,
  setDefaultEmail,
};

const integrationEndpoints = {
  getRechargeIntegration,
  upsertRechargeIntegration,
};

const subscriptionEndpoints = {
  getCustomerSubscriptions,
  editSubscription,
  editSubscriptionAddress,
  cancelSubscription,
  skipSubscription,
  getCustomerSubscriptionTotalSpent,
};

export const rpcDefinition = {
  checkMetaDeletionStatus,
  generateCopilotResponse,
  getBaseSupportPredictedPriceInfo,
  getConversationInsightsAnalytics,
  getCustomerSuggestions,
  getLlmThread,
  getShopperAiDashboardMetrics,
  getShopperAiSettingsOptimization,
  lookupProductsByIds,
  markConversationsSpam,
  regenerateConversationInsights,
  supportAnalytics,
  updateGmailIntegrationName,
  upsertShopperAiSettingsOptimization,
  ...billingEndpoints,
  ...emailEndpoints,
  ...integrationEndpoints,
  ...subscriptionEndpoints,
};

export type RedoSupportRpcDefinition = InferRpcDefinition<typeof rpcDefinition>;
